import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { ShortLangToMapLanguage } from '../../common/converters/language';
import { AzureDataPosition, AzureMap, AzureMapDataSourceProvider, AzureMapFeature, AzureMapLayerProvider, AzureMapPopup, AzureMapsProvider, IAzureMapFeatureType, IAzureMapOptions } from "react-azure-maps";
import { AuthenticationType, data } from 'azure-maps-control'
import { useLocation } from 'react-router-dom';
import { wrapper } from '../../common/styles/formDropDown.styles';
import { useContainerDetailMap } from '../hooks/useContainerDetailMap';
import { Dropdown } from '@fluentui/react';
import { useContainerHistoryPoints } from '../hooks/useContainerHistoryPoints';
import { IContainerHistoryPoint } from '../interfaces/IContainerHistoryPoint';
import { useContainerHistoryMap } from '../hooks/useContainerHistoryMap';
import { ContainerHistoryPopupHtml } from './ContainerHistoryPopupHtml';
import { ContainerDetailPopupHtml } from './ContainerDetailPopupHtml';
import { ContainerTimeline } from './ContainerTimeline';
import { MapGlyphFixer, transformMapRequest } from '../../storeMap/components/MapGlyphFixer';

const { REACT_APP_AZURE_MAP_KEY } = process.env;

const option: IAzureMapOptions = {
    authOptions: {
        authType: AuthenticationType.subscriptionKey,
        subscriptionKey: REACT_APP_AZURE_MAP_KEY
    },
    center: [19.4803, 52.0693],
    zoom: 10,
    transformRequest: transformMapRequest,
}

const ContainerDetailMap: React.FC = () => {
    const { t } = useTranslation("admin");
    const location = useLocation();
    const fromParam = new URLSearchParams(location.search).get('from');
    const fromDate = useMemo(() => (fromParam ? new Date(fromParam) : null), [fromParam]);

    const [daysBack, setDaysBack] = useState<number>(fromDate ? 1 : 30);
    const { initValues } = useContainerDetailMap();
    const { containerHistoryPoints, scanEvents, dateOptions } = useContainerHistoryMap(fromDate);
    const { filteredContainerHistoryPoints, filteredScanEvents } = useContainerHistoryPoints(containerHistoryPoints, scanEvents, daysBack, fromDate);
    option.center = [initValues.longitude, initValues.latitude];
    option.language = ShortLangToMapLanguage(i18next.language);
    const [popupVisible, setPopupVisible] = useState<boolean>(false);
    const [popupPosition, setPopupPosition] = useState<data.Position>(new data.Position(0, 0));

    function showPopup(e: any) {
        if (e.shapes && e.shapes.length > 0) {
            setPopupVisible(true);
            setPopupPosition(createDataPosition(initValues.longitude, initValues.latitude));
        }
    }

    const preparePopupOptions = (pointPosition: any, offset: number = -25) => ({
        position: pointPosition,
        pixelOffset: [0, offset]
    });

    const memoizedMarkerRender = useMemo(
        () => (
            <AzureMapFeature
                key={initValues.id}
                id={initValues.id}
                type="Point"
                coordinate={[initValues.longitude, initValues.latitude]}
                properties={{ id: initValues.id }}
            />
        ),
        [initValues]
    );

    const currentContainerHistoryLinePoints: AzureDataPosition[] = [];
    const [currentContainerHistoryPoints, setCurrentContainerHistoryPoints] = useState<any[]>([]);

    useEffect(() => {
        const points: any[] = [];
        filteredContainerHistoryPoints.map((x: IContainerHistoryPoint) => {
            currentContainerHistoryLinePoints.push(createDataPosition(x.longitude, x.latitude));
            points.push({ id: x.id, key: x.id, longitude: x.longitude, latitude: x.latitude });
        });
        setCurrentContainerHistoryPoints(points);
    }, [filteredContainerHistoryPoints]);

    const memomizedContainerHistoryPointsFunc = React.useMemo(
        () => renderMultiLine('LineString', currentContainerHistoryLinePoints, {}),
        [filteredContainerHistoryPoints]
    );

    const memomizedContainerHistoryPoints = React.useMemo(
        () => renderMultiLine1(currentContainerHistoryPoints),
        [filteredContainerHistoryPoints, currentContainerHistoryPoints]
    );

    const memoizedContainerDetailPopup = React.useMemo(
        () => (
            <AzureMapPopup
                isVisible={popupVisible}
                options={preparePopupOptions(popupPosition)}
                popupContent={<ContainerDetailPopupHtml containerDetail={initValues} />}
            />
        ),
        [popupVisible, popupPosition],
    );

    const createDataPosition = (longitude: number, latitude: number) =>
        new data.Position(longitude, latitude);

    const [selectedPoint, setSelectedPoint] = useState<IContainerHistoryPoint | undefined>();

    const handleSelectPoint = (point: IContainerHistoryPoint) => {
        setSelectedPoint(point);
        setPopupVisible(false);
    };

    const selectedDeviceHistoryPoint = React.useMemo(
        () => (
            <AzureMapFeature
                key={selectedPoint !== undefined ? selectedPoint.id : 0}
                id={selectedPoint !== undefined ? selectedPoint.id.toString() : ""}
                type="Point"
                coordinate={[
                    selectedPoint !== undefined ? selectedPoint.longitude : 0,
                    selectedPoint !== undefined ? selectedPoint.latitude : 0
                ]}
            />
        ), [selectedPoint]
    );

    const selectedDeviceHistoryPopup = React.useMemo(
        () => (
            <AzureMapPopup
                isVisible={true}
                options={preparePopupOptions(createDataPosition(
                    selectedPoint !== undefined ? selectedPoint.longitude : 0,
                    selectedPoint !== undefined ? selectedPoint.latitude : 0), -5)}
                popupContent={<ContainerHistoryPopupHtml containerHistoryPoint={selectedPoint} />}
            />
        ),
        [selectedPoint]
    );

    const onChange = (event: React.FormEvent<HTMLDivElement>, option?: any): void => {
        if (option) setDaysBack(option.days);
    };

    const selectedDropdownKey = dateOptions.find(o => o.days === daysBack)
        ? daysBack
        : 0;

    const outerRef = useRef<HTMLDivElement>(null);
    const controlsRef = useRef<HTMLDivElement>(null);
    const [mapHeight, setMapHeight] = useState<number>(400);

    useLayoutEffect(() => {
        const update = () => {
            if (!outerRef.current || !controlsRef.current) return;
            const available = outerRef.current.clientHeight;
            const used = controlsRef.current.clientHeight;
            const h = available - used;
            if (h > 50) setMapHeight(h);
        };
        update();
        const ro = new (window as any).ResizeObserver(update);
        if (outerRef.current) ro.observe(outerRef.current);
        if (controlsRef.current) ro.observe(controlsRef.current);
        return () => ro.disconnect();
    }, []);

    return (<>
        <div className={wrapper} ref={outerRef} style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
            <div ref={controlsRef}>
            <Dropdown
                placeholder={t("nav.selectDaysCount") as string}
                label={t("nav.daysRangeLabel") as string}
                options={dateOptions}
                onChange={onChange}
                selectedKey={selectedDropdownKey}
            />
            <ContainerTimeline
                historyPoints={filteredContainerHistoryPoints}
                scanEvents={filteredScanEvents}
                selectedPoint={selectedPoint}
                onSelectPoint={handleSelectPoint}
            />
            </div>
            <div style={{ height: mapHeight }}>
                <AzureMapsProvider>
                    <div style={{ position: 'relative', height: '100%' }}>
                        <AzureMap options={option}>
                            <AzureMapDataSourceProvider id={'AzureMapDataSourceProvider main'}>
                                <AzureMapDataSourceProvider id={'AzureMapDataSourceProvider memoizedContainerDetailPopup'}>
                                    <AzureMapLayerProvider id={'AzureMapLayerProvider memoizedContainerDetailPopup'} type={"SymbolLayer"} />
                                    {memoizedContainerDetailPopup}
                                </AzureMapDataSourceProvider>
                                <AzureMapDataSourceProvider id={'DataSource Provider1222'}>
                                    <AzureMapLayerProvider id={'shape AzureMapLayerProvider1162'} type={"LineLayer"} />
                                    {memomizedContainerHistoryPointsFunc}
                                </AzureMapDataSourceProvider>
                                <AzureMapDataSourceProvider id={'DataSource Provider2'}>
                                    <AzureMapLayerProvider
                                        options={{ radius: 6, strokeWidth: 0, color: "#0066CC" }}
                                        id={'shape AzureMapLayerProvider2'}
                                        type={'BubbleLayer'}
                                    />
                                    {memomizedContainerHistoryPoints}
                                </AzureMapDataSourceProvider>
                                {selectedPoint !== undefined ?
                                    <AzureMapDataSourceProvider id={'DataSource Provider222'}>
                                        <AzureMapLayerProvider
                                            options={{ radius: 9, strokeWidth: 0, color: "#0066CC" }}
                                            id={'shape AzureMapLayerProvider222'}
                                            type={'BubbleLayer'}
                                        />
                                        {selectedDeviceHistoryPoint}
                                    </AzureMapDataSourceProvider>
                                    : <></>}
                                {selectedPoint !== undefined ?
                                    <AzureMapDataSourceProvider id={'DataSource Provider12211'}>
                                        <AzureMapLayerProvider id={'shape AzureMapLayerProvider11211'} type={"SymbolLayer"} />
                                        {selectedDeviceHistoryPopup}
                                    </AzureMapDataSourceProvider>
                                    : <></>}
                                <AzureMapLayerProvider
                                    id={'shape AzureMapLayerProvider11'}
                                    events={{ click: showPopup }}
                                    type={"SymbolLayer"}
                                />
                                {memoizedMarkerRender}
                            </AzureMapDataSourceProvider>
                            <MapGlyphFixer detail />
                        </AzureMap>
                    </div>
                </AzureMapsProvider>
            </div>
        </div>
    </>);
};

export default ContainerDetailMap;

export const renderMultiLine = (
    type: IAzureMapFeatureType,
    coordinates: data.Position[],
    properties: Record<string, unknown>,
) => {
    const rendId = Math.random();
    return (
        <AzureMapFeature
            key={rendId}
            id={rendId.toString()}
            type={type}
            coordinates={coordinates}
            properties={properties}
        />
    );
};

export const renderMultiLine1 = (points: any[]) => (
    <>
        {points.map(x => {
            const popupOptions = { position: new data.Position(x.longitude, x.latitude) };
            return (
                <AzureMapFeature
                    key={x.key}
                    id={x.id.toString()}
                    variant={'shape'}
                    type="Point"
                    coordinate={popupOptions.position}
                />
            );
        })}
    </>
);
